<template>
  <v-card variant="outlined">
    <v-card-title v-if="showHeader" class="d-flex flex-wrap align-center justify-space-between ga-3">
      <div>
        <div class="text-subtitle-1 font-weight-bold">
          {{ editorTitle }}
        </div>
        <div class="text-body-2 text-medium-emphasis">
          {{ editorSubtitle }}
        </div>
      </div>
      <div class="d-flex flex-wrap ga-2">
        <v-chip color="info">
          {{ localForm.routing_mode || "store_only" }}
        </v-chip>
        <v-chip color="primary">
          {{ localForm.content_type || "application/json" }}
        </v-chip>
      </div>
    </v-card-title>
    <v-divider v-if="showHeader" />
    <v-card-text>
      <v-alert v-if="formError" type="error" class="mb-4">
        {{ formError }}
      </v-alert>

      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            ref="nameField"
            v-model="localForm.name"
            :label="$t('ncrm.integrations.externalApi.editor.fields.name')"
            hide-details="auto"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="localForm.endpoint_key"
            :label="$t('ncrm.integrations.externalApi.editor.fields.endpointKey')"
            hide-details="auto"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            v-model="localForm.template_key"
            :items="templateItems"
            item-title="label"
            item-value="value"
            :label="$t('ncrm.integrations.externalApi.editor.fields.template')"
            hide-details="auto"
            :menu-props="selectMenuProps"
            @update:model-value="handleTemplateChange"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            v-model="localForm.status"
            :items="statusItems"
            :label="$t('ncrm.integrations.externalApi.editor.fields.status')"
            hide-details="auto"
            :menu-props="selectMenuProps"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-select
            v-model="localForm.entity_scope"
            :items="scopeItems"
            :label="$t('ncrm.integrations.externalApi.editor.fields.scope')"
            hide-details="auto"
            :menu-props="selectMenuProps"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="localForm.source_system"
            :label="$t('ncrm.integrations.externalApi.editor.fields.sourceSystem')"
            hide-details="auto"
          />
        </v-col>
        <v-col cols="12">
          <v-textarea
            v-model="localForm.description"
            :label="$t('ncrm.integrations.externalApi.editor.fields.description')"
            rows="2"
            hide-details="auto"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="rateLimitPerMinute"
            :label="$t('ncrm.integrations.externalApi.editor.fields.rateLimit')"
            type="number"
            min="1"
            hide-details="auto"
          />
        </v-col>
        <v-col cols="12" md="6" class="d-flex align-center">
          <v-switch
            v-model="localForm.allow_source_system_override"
            color="primary"
            inset
            :label="$t('ncrm.integrations.externalApi.editor.fields.allowSourceOverride')"
            hide-details
          />
        </v-col>
        <v-col cols="12">
          <v-textarea
            v-model="mappingJsonText"
            :label="$t('ncrm.integrations.externalApi.editor.fields.mappingJson')"
            rows="10"
            no-resize
            spellcheck="false"
            class="ncrm-external-api-endpoint-editor__json-field"
            hide-details="auto"
          />
        </v-col>
      </v-row>

      <template v-if="showSaveAction">
        <div class="d-flex flex-wrap align-center justify-space-between ga-3 mt-4">
          <div class="text-body-2 text-medium-emphasis">
            {{ endpointSummary }}
          </div>
          <v-btn color="primary" :loading="saving" @click="emitSave">
            <v-icon start>mdi-content-save-outline</v-icon>
            {{ resolvedSaveLabel }}
          </v-btn>
        </div>
      </template>

      <v-divider class="my-6" />

      <NcrmExternalApiEndpointSamplesCard
        :endpoint="localForm"
        :templates="templates"
      />

      <template v-if="showKeyManagement">
        <v-divider class="my-6" />

        <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-4">
          <div>
            <div class="text-subtitle-2 font-weight-bold">
              {{ $t("ncrm.integrations.externalApi.keys.title") }}
            </div>
            <div class="text-body-2 text-medium-emphasis">
              {{ $t("ncrm.integrations.externalApi.keys.subtitle") }}
            </div>
          </div>
        </div>

        <v-alert
          v-if="generatedSecret"
          type="warning"
          class="mb-4"
        >
          <div class="font-weight-bold mb-2">
            {{ $t("ncrm.integrations.externalApi.keys.generatedTitle") }}
          </div>
          <div class="text-body-2 mb-2">
            {{ $t("ncrm.integrations.externalApi.keys.generatedBody") }}
          </div>
          <code class="ncrm-external-api-endpoint-editor__secret">{{ generatedSecret }}</code>
        </v-alert>

        <template v-if="hasPersistedEndpoint">
          <div class="d-flex flex-wrap align-center ga-3 mb-4">
            <v-text-field
              ref="keyLabelField"
              v-model="newKeyLabel"
              :label="$t('ncrm.integrations.externalApi.keys.fields.label')"
              :error-messages="resolvedKeyErrorMessages"
              hide-details="auto"
              class="flex-grow-1"
              @update:model-value="handleKeyLabelInput"
            />
            <v-btn color="primary" variant="tonal" :loading="keysBusy" @click="emitGenerateKey">
              <v-icon start>mdi-key-plus</v-icon>
              {{ $t("ncrm.integrations.externalApi.keys.actions.generate") }}
            </v-btn>
          </div>

          <v-list v-if="keys.length" lines="two" density="comfortable" class="pa-0">
            <v-list-item
              v-for="key in keys"
              :key="key.id"
              class="mb-2"
              rounded="lg"
            >
              <v-list-item-title class="font-weight-medium">
                {{ key.label }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ key.key_prefix }}
              </v-list-item-subtitle>
              <template #append>
                <div class="d-flex flex-wrap align-center ga-2">
                  <v-chip size="small" :color="statusMeta(key.status).color" :variant="statusMeta(key.status).variant">
                    {{ key.status }}
                  </v-chip>
                  <v-btn
                    size="small"
                    variant="text"
                    color="warning"
                    :disabled="key.status !== 'active' || keysBusy"
                    @click="$emit('revoke-key', key.id)"
                  >
                    {{ $t("ncrm.integrations.externalApi.keys.actions.revoke") }}
                  </v-btn>
                  <v-btn
                    size="small"
                    variant="text"
                    color="error"
                    :disabled="key.status === 'deleted' || keysBusy"
                    @click="$emit('delete-key', key.id)"
                  >
                    {{ $t("ncrm.integrations.externalApi.keys.actions.delete") }}
                  </v-btn>
                </div>
              </template>
            </v-list-item>
          </v-list>
          <div v-else class="py-4 text-body-2 text-medium-emphasis">
            {{ $t("ncrm.integrations.externalApi.keys.empty") }}
          </div>
        </template>

        <v-alert v-else type="info">
          {{ $t("ncrm.integrations.externalApi.keys.saveFirst") }}
        </v-alert>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  buildExternalApiEndpointPayload,
  cloneExternalApiEndpoint,
  findExternalApiTemplate,
  prettyPrintExternalApiJson,
  resolveExternalApiStatusMeta,
} from "@/Utile/ncrmExternalApi.mjs";
import NcrmExternalApiEndpointSamplesCard from "@/components/ncrm/integrations/NcrmExternalApiEndpointSamplesCard.vue";
import "./NcrmExternalApiEndpointEditorCard.css";

export default {
  name: "NcrmExternalApiEndpointEditorCard",
  components: {
    NcrmExternalApiEndpointSamplesCard,
  },
  props: {
    endpoint: {
      type: Object,
      default: null,
    },
    templates: {
      type: Array,
      default: () => [],
    },
    saving: {
      type: Boolean,
      default: false,
    },
    keysBusy: {
      type: Boolean,
      default: false,
    },
    keys: {
      type: Array,
      default: () => [],
    },
    generatedSecret: {
      type: String,
      default: "",
    },
    keyError: {
      type: String,
      default: "",
    },
    statusOptions: {
      type: Array,
      default: () => ["draft", "active", "paused", "archived"],
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    showSaveAction: {
      type: Boolean,
      default: true,
    },
    showKeyManagement: {
      type: Boolean,
      default: true,
    },
    saveLabel: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      formError: "",
      keyFormError: "",
      localForm: cloneExternalApiEndpoint(this.endpoint),
      mappingJsonText: prettyPrintExternalApiJson(this.endpoint?.mapping_config_json || {}),
      rateLimitPerMinute:
        this.endpoint?.rate_limit_config_json?.requests_per_minute || 60,
      newKeyLabel: "",
    };
  },
  computed: {
    hasPersistedEndpoint() {
      return Boolean(this.localForm?.id);
    },
    editorTitle() {
      return this.hasPersistedEndpoint
        ? this.$t("ncrm.integrations.externalApi.editor.titleEdit")
        : this.$t("ncrm.integrations.externalApi.editor.titleCreate");
    },
    editorSubtitle() {
      return this.hasPersistedEndpoint
        ? this.$t("ncrm.integrations.externalApi.editor.subtitleEdit")
        : this.$t("ncrm.integrations.externalApi.editor.subtitleCreate");
    },
    endpointSummary() {
      return this.hasPersistedEndpoint
        ? this.$t("ncrm.integrations.externalApi.editor.summaryExisting", {
            keys: this.keys.length,
            deliveries: this.localForm.delivery_count_24h || 0,
          })
        : this.$t("ncrm.integrations.externalApi.editor.summaryCreate");
    },
    templateItems() {
      return (this.templates || []).map((template) => ({
        label: template.label,
        value: template.key,
      }));
    },
    statusItems() {
      return [...this.statusOptions];
    },
    scopeItems() {
      return ["company", "person"];
    },
    selectMenuProps() {
      return {
        maxHeight: 320,
        contentClass: "ncrm-external-api-endpoint-editor__select-menu",
        zIndex: 2710,
      };
    },
    resolvedSaveLabel() {
      return this.saveLabel || this.$t("ncrm.integrations.externalApi.editor.actions.save");
    },
    resolvedKeyErrorMessages() {
      const message = String(this.keyFormError || this.keyError || "").trim();
      return message ? [message] : [];
    },
  },
  watch: {
    endpoint: {
      immediate: true,
      deep: true,
      handler(nextValue) {
        this.localForm = cloneExternalApiEndpoint(nextValue);
        this.mappingJsonText = prettyPrintExternalApiJson(this.localForm.mapping_config_json || {});
        this.rateLimitPerMinute =
          this.localForm.rate_limit_config_json?.requests_per_minute || 60;
        this.formError = "";
        this.keyFormError = "";
      },
    },
  },
  methods: {
    statusMeta(status) {
      return resolveExternalApiStatusMeta(status);
    },
    handleTemplateChange(templateKey) {
      const template = findExternalApiTemplate(this.templates, templateKey);
      if (!template) return;
      this.localForm.template_key = template.key;
      this.localForm.entity_scope = template.entity_scope;
      this.mappingJsonText = prettyPrintExternalApiJson(template.mapping_config_json || {});
    },
    emitSave() {
      try {
        const payload = this.buildPayloadForSave();
        this.$emit("save", payload);
      } catch (error) {
        this.formError =
          error?.message ||
          this.$t("ncrm.integrations.externalApi.editor.saveError");
      }
    },
    buildPayloadForSave() {
      this.formError = "";
      return buildExternalApiEndpointPayload(this.localForm, {
        mappingText: this.mappingJsonText,
        rateLimitPerMinute: this.rateLimitPerMinute,
      });
    },
    setFormError(message) {
      this.formError =
        message ||
        this.$t("ncrm.integrations.externalApi.editor.saveError");
    },
    handleKeyLabelInput() {
      this.keyFormError = "";
    },
    emitGenerateKey() {
      if (!String(this.newKeyLabel || "").trim()) {
        this.keyFormError = this.$t("ncrm.integrations.externalApi.keys.labelRequired");
        this.$nextTick(() => {
          this.$refs.keyLabelField?.focus?.();
        });
        return;
      }
      this.keyFormError = "";
      this.$emit("generate-key", this.newKeyLabel.trim());
      this.newKeyLabel = "";
    },
    focusNameField() {
      this.$refs.nameField?.focus?.();
    },
  },
};
</script>
